"use client";

import { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Database, Download, Loader2, FileSpreadsheet, Smartphone } from "lucide-react";
import { generateCSV, downloadCSV } from "@/lib/export-utils";
import { format } from "date-fns";
import { toast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/errors";

export function DataSection() {
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingIOS, setIsExportingIOS] = useState(false);
  const blocks = useQuery(api.dataExport.getAllBlocksForExport);
  const iosData = useQuery(api.iosExport.exportAllData);

  const handleExport = async () => {
    if (!blocks || blocks.length === 0) return;

    setIsExporting(true);
    try {
      const csv = generateCSV(blocks);
      const filename = `flextrack-export-${format(new Date(), "yyyy-MM-dd")}.csv`;
      downloadCSV(csv, filename);
      toast({
        title: "Export complete",
        description: `Downloaded ${blocks.length} blocks as CSV`,
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleIOSExport = async () => {
    if (!iosData) return;

    setIsExportingIOS(true);
    try {
      const json = JSON.stringify(iosData, null, 2);
      await navigator.clipboard.writeText(json);
      toast({
        title: "Copied to clipboard!",
        description: "Paste this JSON in the FlexTrack iOS app (Settings → Import)",
      });
    } catch (error) {
      // Fallback: download as file
      const blob = new Blob([JSON.stringify(iosData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flextrack-ios-export-${format(new Date(), "yyyy-MM-dd")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast({
        title: "Export complete",
        description: "JSON file downloaded. Open it and copy contents to iOS app.",
      });
    } finally {
      setIsExportingIOS(false);
    }
  };

  const blockCount = blocks?.length ?? 0;
  const isLoading = blocks === undefined;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Data</CardTitle>
        </div>
        <CardDescription>Export and manage your data</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Export Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Export All Data</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {isLoading
                  ? "Loading..."
                  : `${blockCount} block${blockCount !== 1 ? "s" : ""} available`}
              </p>
            </div>
          </div>

          <Button
            type="button"
            onClick={handleExport}
            disabled={isExporting || isLoading || blockCount === 0}
            variant="outline"
            className="w-full"
          >
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Download CSV
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground">
            Includes date, times, earnings, mileage, station, and notes for all
            blocks.
          </p>
        </div>

        {/* iOS App Export */}
        <div className="pt-4 border-t space-y-3">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2">
                <Smartphone className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">Export for iOS App</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {iosData === undefined ? "Loading..." : iosData === null ? "Sign in to export" : `${iosData.blocks.length} blocks, ${iosData.expenses.length} expenses`}
              </p>
            </div>
          </div>

          <Button
            type="button"
            onClick={handleIOSExport}
            disabled={isExportingIOS || iosData === undefined || iosData === null}
            variant="outline"
            className="w-full"
          >
            {isExportingIOS ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Copying...
              </>
            ) : (
              <>
                <Smartphone className="h-4 w-4 mr-2" />
                Copy JSON for iOS App
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground">
            Copies data to clipboard. Paste in FlexTrack iOS app under Settings → Import.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
